/**
 * useThreeViewer — V14.63 Sprint 26F (vanilla three.js hook).
 *
 * Mount a vanilla three.js scene onto a <canvas ref> WITHOUT ever using
 * React-Three-Fiber JSX, because the Emergent dev preview's source-tracking
 * babel plugin injects `x-line-number`, `x-source-file`, … on every JSX
 * element in our app source. R3F's `applyProps` interprets attribute names
 * with a `-` as nested-path setters on THREE objects (e.g. `position-x` →
 * `obj.position.x`) which turns `x-line-number` into `obj.x.lineNumber` —
 * and `x` is a scalar number on Vector3/Position → throw.
 *
 * By using vanilla three.js (manual scene/camera/renderer + OrbitControls
 * from `three/examples/jsm`), we render imperatively on a single DOM
 * `<canvas>` and never let R3F's reconciler touch our JSX.
 *
 * Usage :
 *   const { canvasRef, renderer } = useThreeViewer({
 *     onCreate({ scene, camera, controls, renderer }) {
 *       // imperative scene building here
 *       return () => { ... cleanup ... };
 *     },
 *     deps: [floor],  // rebuilds when deps change
 *     cameraInit: { pos: [10, 8, 10], target: [0, 1, 0] },
 *   });
 *   return <canvas ref={canvasRef} className="w-full h-full" />;
 */
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export const useThreeViewer = ({
  onCreate,
  deps = [],
  cameraInit = { pos: [10, 8, 10], target: [0, 0, 0] },
  background = '#0b1220',
  enableShadows = true,
  fov = 38,
} = {}) => {
  const canvasRef = useRef(null);
  const stateRef = useRef({});
  const [ready, setReady] = useState(false);

  // 1. Mount the renderer + base scene once.
  useEffect(() => {
    if (!canvasRef.current) return undefined;
    const canvas = canvasRef.current;
    const renderer = new THREE.WebGLRenderer({
      canvas, antialias: true, preserveDrawingBuffer: true, alpha: false,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.shadowMap.enabled = enableShadows;
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(background);
    const camera = new THREE.PerspectiveCamera(fov, 1, 0.1, 5000);
    camera.position.set(...cameraInit.pos);
    camera.lookAt(...cameraInit.target);

    const controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.target.set(...cameraInit.target);
    controls.maxPolarAngle = Math.PI / 2.05;
    controls.update();

    // Resize handling.
    const ro = new ResizeObserver(() => {
      const r = canvas.getBoundingClientRect();
      const w = Math.max(1, r.width);
      const h = Math.max(1, r.height);
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    });
    ro.observe(canvas);
    // Initial sizing.
    {
      const r = canvas.getBoundingClientRect();
      renderer.setSize(Math.max(1, r.width), Math.max(1, r.height), false);
      camera.aspect = (Math.max(1, r.width)) / (Math.max(1, r.height));
      camera.updateProjectionMatrix();
    }

    let raf = 0;
    const animate = () => {
      controls.update();
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    stateRef.current = { scene, camera, controls, renderer };
    setReady(true);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      controls.dispose();
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
          else obj.material.dispose();
        }
      });
      renderer.dispose();
      stateRef.current = {};
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Run `onCreate` whenever `deps` change.
  useEffect(() => {
    if (!ready) return undefined;
    const { scene, camera, controls, renderer } = stateRef.current;
    if (!scene || !onCreate) return undefined;
    const cleanup = onCreate({ scene, camera, controls, renderer });
    return () => {
      if (typeof cleanup === 'function') cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, ...deps]);

  return { canvasRef, stateRef };
};

/**
 * Helper to dispose a group's GPU resources before removing it from its parent.
 */
export const disposeGroup = (group) => {
  group.traverse((obj) => {
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) {
      if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
      else obj.material.dispose();
    }
  });
  if (group.parent) group.parent.remove(group);
};
